import * as signalR from '@microsoft/signalr';
import { useEffect, useRef, useState } from 'react';
import useLocales from '../hooks/useLocales';
import {
    Box,
    Stack,
    List,
    Badge,
    Button,
    Divider,
    Typography,
    ListItemText,
    ListItemAvatar,
    ListItemButton,
    LinearProgress,
    Alert,
    Tooltip,
} from '@mui/material';
import { fToNow } from '../utils/formatTime';

import Iconify from '../components/Iconify';
import Scrollbar from '../components/Scrollbar';
import IconButtonAnimate from '../components/animate/IconButtonAnimate';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CustomAvatar from '../components/custom-avatar/CustomAvatar';
import { getNotificationsAsync, getAmountOfUnreadConversationsAsync } from '../Common/api-clients/notificationClient';
import { getAppSettings, getSelectedClient } from '../Common/Utils/localStorageHelper';
import { AppState, UpdateMarkConversationAsReadActionPayload, UpdateNotificationsActionPayload, UpdateUnreadNotificationItemsCountActionPayload } from '../Common/Reducer/rootReducerTypes';
import { useDispatch, useSelector } from 'react-redux';
import MenuPopover from '../components/menu-popover/MenuPopover';
import { markConversationAsRead, updateNotifications, updateUnreadNotificationItemsCount } from '../Common/Reducer/notificationActionAndReducer';
import ErrorIcon from '@mui/icons-material/Error';
import { SmallTalkPermission, OrderStatus, AuthMethod } from '../Common/enums';
import useBackendData from '../hooks/useBackendData';
import { addMessageListener, deleteMessageListener, startChatHubConnection } from '../middlewares/chatHub';
import { getPermissions } from '../utils/userInfoKeyValuePairChecker';
import { isNotNullAndUndefined } from '../Common/Utils/NullCheckHelpers';
import { getBackendDataListAzureB2C, getBackendDataListIDM } from '../Common/Utils/backendDataHelper';
import { buildHttpHeaders } from '../utils/buildHttpHeaders';

export default function NotificationsPopover() {
    const dispatch = useDispatch();
    const MAX_NOTIFICATION_ELEMENTS = 5;
    const user = useSelector((state: AppState) => state.user);
    const { translate } = useLocales();
    const anchorRef = useRef(null);
    const notifications = useSelector((state: AppState) => state.notificationList.notifications);
    const totalUnReadConversations = useSelector((state: AppState) => state.notificationList.unreadItems);
    const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);
    const [chatAppAvailable, setChatAppAvailable] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hmChatBaseUrl, setHmChatBaseUrl] = useState<string | null>(null);
    const [hubConnection, setHubConnection] = useState<signalR.HubConnection | null>(null);
    const [refreshConversationList, setRefreshConversationList] = useState<boolean>(false);

    const userInfo = useSelector((state: AppState) => state.userInfo.userInformation);
    const smallTalkPermissions = getPermissions(userInfo.userPermissions, 'smallTalkPermissions');

    const hasPermissionToShowMessages = smallTalkPermissions !== null && smallTalkPermissions.includes(SmallTalkPermission.show_messages)
        && smallTalkPermissions.includes(SmallTalkPermission.view_identity_customers);
    const [selectedClient] = useBackendData();

    const handleClickOnNotification = async (notification?: NotificationItem | null) => {
        const chatAppBaseUrl = `${hmChatBaseUrl}`;
        let urlParams = '';

        if (notification) {
            urlParams = `?contextId=${notification.contextId}&contextType=${notification.contextType}&conversationType=${notification.conversationType}`;
        }

        if (user && user.isAuthenticated) {
            let backendDataList = [];
            if (getAppSettings().authMethod === AuthMethod.IDM) {
                backendDataList = getBackendDataListIDM(user);
            } else {
                backendDataList = await getBackendDataListAzureB2C();
            }

            if (backendDataList && backendDataList.length > 1) {
                urlParams += `${urlParams ? '&' : '?'}selectedClient=${selectedClient.name}`;
            }
        }

        const finalUrl = chatAppBaseUrl + urlParams;
        window.open(finalUrl, 'child-window');

        if (notification) {
            const payload: UpdateMarkConversationAsReadActionPayload = { notificationId: notification.id };
            dispatch(markConversationAsRead(payload));
        }
    };


    const handleNotificationPopoverState = (currentTarget: HTMLElement | null) => {
        setOpenPopover(currentTarget);
    }

    const getUnreadConversations = (controller: AbortController) => {
        getAmountOfUnreadConversationsAsync(getSelectedClient().name, controller.signal)
            .then((result: number) => {
                const maxUnreadMessagesToShow = 5;
                const payload: UpdateUnreadNotificationItemsCountActionPayload = { unreadItems: result > maxUnreadMessagesToShow ? maxUnreadMessagesToShow : result }
                dispatch(updateUnreadNotificationItemsCount(payload));
            });
    }

    useEffect(() => {
        const controller = new AbortController();
        if (user && user.isAuthenticated) {
            if (!openPopover) {
                getUnreadConversations(controller);
            } else {
                try {
                    setIsLoading(true);
                    getNotificationsAsync(getSelectedClient().name, controller.signal)
                        .then((data: { conversationsList: Array<any>, conversationType: number }) => {
                            const payload: UpdateNotificationsActionPayload = { notifications: data.conversationsList };
                            dispatch(updateNotifications(payload));
                            setChatAppAvailable(true);
                        })
                        .catch((error) => {
                            console.log(error);
                            setChatAppAvailable(false);
                        })
                        .finally(() => {
                            setIsLoading(false);
                        });
                } catch (error) {
                    console.log(error);
                    setChatAppAvailable(false);
                    setIsLoading(false);
                }
            }
        }

        return () => controller.abort();

    }, [openPopover, user]);

    useEffect(() => {
        if (user && user.isAuthenticated && hasPermissionToShowMessages) {

            fetch('settings/hmchaturl', {
                headers: buildHttpHeaders(getSelectedClient().name),
            })
                .then(response => response.text())
                .then((data) => {
                    setHmChatBaseUrl(data.toString());
                    localStorage.setItem("hmChatBaseUrl", data.toString());
                })
                .then(() => {
                    if (hmChatBaseUrl) {
                        const connection = new signalR.HubConnectionBuilder()
                            .withUrl(`${hmChatBaseUrl}/chatHub`, {
                                headers: {
                                    'Backend': selectedClient?.backendName,
                                    'AuthMethod': getAppSettings().authMethod
                                }
                            })
                            .withAutomaticReconnect()
                            .build();

                        setHubConnection(connection);
                    }
                });
        }
    }, [user, hmChatBaseUrl, hasPermissionToShowMessages]);

    useEffect(() => {
        if (user?.profile?.sub && hubConnection && hasPermissionToShowMessages) {
            startChatHubConnection(user?.profile?.sub, hubConnection);
            addMessageListener(() => {
                setRefreshConversationList(true);
            }, hubConnection);

            deleteMessageListener(() => {
                setRefreshConversationList(true);
            }, hubConnection);
        }
    }, [user, hubConnection, hasPermissionToShowMessages]);

    useEffect(() => {
        if (refreshConversationList) {
            const controller = new AbortController();

            if (user && user.isAuthenticated && hubConnection && hasPermissionToShowMessages) {
                if (!openPopover) {
                    getUnreadConversations(controller);
                } else {
                    getNotificationsAsync(getSelectedClient().name, controller.signal)
                        .then((data: { conversationsList: Array<any>, conversationType: number }) => {
                            const payload: UpdateNotificationsActionPayload = { notifications: data.conversationsList };
                            dispatch(updateNotifications(payload));
                        });
                }
            }

            setRefreshConversationList(false);
        }
    }, [refreshConversationList]);

    return (
        <>
            <IconButtonAnimate
                color={openPopover ? 'primary' : 'default'}
                ref={anchorRef}
                onClick={(event) => handleNotificationPopoverState(event.currentTarget)}
                sx={{ width: 40, height: 40 }}
            >
                <Badge badgeContent={totalUnReadConversations} color="error">
                    <Iconify icon="eva:bell-fill" />
                </Badge>
            </IconButtonAnimate>

            <MenuPopover
                open={openPopover}
                anchorEl={anchorRef.current}
                onClose={() => handleNotificationPopoverState(null)}
                sx={chatAppAvailable ? { height: 500 } : { height: 100 }}
            >
                {isLoading && <LinearProgress />}
                {chatAppAvailable && !isLoading ?
                    <>
                        <Button sx={{ width: '100%' }} disableRipple onClick={() => handleClickOnNotification()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">{translate('Bell.Messages')} &nbsp;</Typography>
                                        <OpenInNewIcon />
                                    </Box>

                                    {totalUnReadConversations > 0 ?
                                        <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'none' }}>
                                            {/* {translate('Bell.YouHaveUnreadMessages', { amount: totalUnReadConversations.toString() })} */}
                                            {translate('Bell.MostRecent', { amount: totalUnReadConversations.toString() })}
                                        </Typography>
                                        :
                                        <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'none' }}>
                                            {translate('Bell.YouHaveNoNewMessages')}
                                        </Typography>
                                    }
                                </Box>
                            </Box>
                        </Button>

                        <Divider sx={{ borderStyle: 'dashed' }} />

                        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
                            <List
                                disablePadding
                            >
                                {notifications.slice(0, MAX_NOTIFICATION_ELEMENTS).map((notification: NotificationItem) => {
                                    return (
                                        notification.lastMessage &&
                                        <NotificationItem
                                            key={notification.id}
                                            notification={notification}
                                            handleClickOnNotificationItem={() => handleClickOnNotification(notification)}
                                        />
                                    )
                                })}
                            </List>
                        </Scrollbar>
                        <Divider sx={{ borderStyle: 'dashed' }} />
                    </>
                    : !isLoading ?
                        <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: 2.5 }}>
                            <Alert severity="error">{translate('Bell.ChatAppIsNotAvailable')}</Alert>
                        </Box>
                        :
                        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
                            <Alert severity="info">{translate('Bell.Loading')}</Alert>
                        </Box>
                }
            </MenuPopover>
        </>
    );
}

// ----------------------------------------------------------------------

type Message = {
    attachments: Array<any>;
    chatConversationId: number;
    id: number;
    senderId: string;
    senderName: string;
    senderEmail: string;
    senderIsEmployee: boolean;
    message: string;
    sentAt: Date;
    isImportant: boolean;
    isRead: boolean;
}

export type NotificationItem = {
    backOfficeId: string | null;
    contextType: number;
    conversationType: number;
    customerBackOfficeId: string | null;
    customerName: string | null;
    contextId: string;
    id: number;
    name: string | null;
    lastMessage: Message;
    status: any;
};

function NotificationItem({ notification, handleClickOnNotificationItem }: { notification: NotificationItem, handleClickOnNotificationItem: (e: any) => void }) {
    const { title } = renderContent(notification);
    const userInfo = useSelector((state: AppState) => state.userInfo.userInformation);
    const MAX_MESSAGEBODY_CHARACTERS = 24;

    return (
        <ListItemButton
            sx={{
                py: 1.5,
                px: 2.5,
                mt: '1px'
            }}
            onClick={handleClickOnNotificationItem}
        >
            <ListItemAvatar>
                {notification.lastMessage?.senderName &&
                    <Tooltip title={notification.lastMessage?.senderName}>
                        <CustomAvatar
                            alt={notification.lastMessage?.senderName}
                            name={notification.lastMessage?.senderName}
                            currentIsEmployee={notification.lastMessage?.senderIsEmployee}
                            loggedInIsEmployee={isNotNullAndUndefined(userInfo.employeeId)}
                            status={notification.status}
                        />
                    </Tooltip>
                }
            </ListItemAvatar>

            <ListItemText
                primary={title}
                primaryTypographyProps={{
                    noWrap: true,
                    variant: 'subtitle2',
                    fontStyle: notification?.status === OrderStatus.Passive ? 'italic' : 'normal',
                    color: notification?.status === OrderStatus.Passive ? 'text.disabled' : 'text.primary'
                }}
                secondaryTypographyProps={{
                    noWrap: true,
                    variant: notification.lastMessage.isRead ? 'body2' : 'subtitle2',
                    color: notification.lastMessage.isRead ? 'text.secondary' : notification?.status === OrderStatus.Passive ? 'text.disabled' : 'text.primary',
                    fontStyle: notification?.status === OrderStatus.Passive ? 'italic' : 'normal'
                }}
                secondary={
                    <Stack direction="row" sx={{ mt: 0.5, typography: 'caption' }}>
                        {truncate(notification.lastMessage?.senderName, MAX_MESSAGEBODY_CHARACTERS)}
                        : &nbsp;
                        {truncate(notification.lastMessage?.message, MAX_MESSAGEBODY_CHARACTERS)}
                    </Stack>
                }
            />
        </ListItemButton>
    );
}


// ----------------------------------------------------------------------

function renderContent(notification: NotificationItem) {
    const MAX_CONTEXT_BACKOFFICEID_CHARACTERS = 8;
    const MAX_CONTEXT_NAME_CHARACTERS = 20;
    const isImportantMessage = notification.lastMessage?.isImportant;
    const { translate } = useLocales();

    const getMessageDetails = () => {
        if (notification.backOfficeId && notification.name) {
            return (
                <Typography component="span" variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {truncate(notification.backOfficeId, MAX_CONTEXT_BACKOFFICEID_CHARACTERS) + ' - ' + truncate(notification.name, MAX_CONTEXT_NAME_CHARACTERS)}
                </Typography>
            );
        } else if (notification.backOfficeId) {
            return (
                <Typography component="span" variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {truncate(notification.backOfficeId, MAX_CONTEXT_BACKOFFICEID_CHARACTERS)}
                </Typography>
            );
        } else if (notification.name) {
            return (
                <Typography component="span" variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {truncate(notification.name, MAX_CONTEXT_NAME_CHARACTERS)}
                </Typography>
            );
        } else {
            return (
                <Typography component="span" variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {'Unknown'}
                </Typography>
            );
        }
    }

    const title = (
        <Typography variant="subtitle2">
            {notification.lastMessage && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {isImportantMessage && <ErrorIcon style={{ color: 'red' }} />}
                    {getMessageDetails()}
                    <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: 'text.secondary', marginLeft: 'auto' }}
                    >
                        {notification.lastMessage?.sentAt && notification?.status !== OrderStatus.Passive && (
                            <Typography variant="caption">&nbsp; {fToNow(notification.lastMessage?.sentAt)}</Typography>
                        )}
                        {notification.lastMessage?.sentAt && notification?.status === OrderStatus.Passive && (
                            <Typography sx={{ color: 'error.main', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                {translate('Bell.Closed')}
                            </Typography>
                        )}
                    </Typography>
                </div>
            )}
        </Typography>
    );

    return {
        title: title,
    };
}

function truncate(str: string, maxChars: number) {
    if (str && str.length > maxChars) {
        return str.substring(0, maxChars) + '...';
    }

    return str;
}